import { useState, useContext, useEffect } from 'react';

import { ReactComponent as ActionAlert } from '../../../assets/icons/action-alert.svg';
import { ReactComponent as AlertIcon } from '../../../assets/icons/alert-icon.svg';
import { ReactComponent as ActionAlertDark } from '../../../assets/icons/iconsDark/action-calender-icon-dark.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import ThemeContext from '../../../contexts/theme-context/ThemeContext';
import {
  AvailableAlerts,
  FilterAlertInterFace,
  RequestAlertInterFace,
} from '../../../interface/alerts.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllAlerts, setAlertFilterData } from '../../../redux/slice/AlertManagementSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';

import AlterFilter from './AlterFilters';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}

/**
 * Alerts page
 */
function Alerts() {
  const { theme } = useContext(ThemeContext);
  const dispatch = useAppDispatch();
  const { alerts, loading, page, limit, totalPages, totalResults, filter } = useAppSelector(
    (state) => state.alerts,
  );
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });

  /**
   * Fetch alert data function
   * @param data
   */
  const fetchData = (data: RequestAlertInterFace) => {
    if (!loading) {
      dispatch(fetchAllAlerts(data));
    }
  };

  /**
   * Handle the clear function
   */
  const handleClearFilter = () => {
    dispatch(setAlertFilterData({}));
    fetchData({
      limit,
      page: DefaultApiLimit.page,
    });
  };
  /**
   * Handle the sort order function
   */
  const handleSortFunction = (sortData: any) => {
    dispatch(setAlertFilterData(sortData));
    fetchData({
      ...sortData,
      limit,
      page: DefaultApiLimit.page,
    });
  };

  /**
   * Handle the submit filter modal function
   * @param filterData
   */
  const handleSubmitFilter = (filterData: FilterAlertInterFace) => {
    dispatch(setAlertFilterData(filterData));
    fetchData({
      ...filterData,
      limit,
      page: DefaultApiLimit.page,
    });
  };

  /**
   * handlePageChange
   */
  const handlePageChange = (pageNumber: number) => {
    fetchData({
      ...filter,
      limit,
      page: pageNumber,
    });
  };
  /**
   * handleLimitChange
   */
  const handleLimitChange = (limitCount: number) => {
    fetchData({
      ...filter,
      limit: limitCount,
      page: DefaultApiLimit.page,
    });
  };
  /**
   * filter
   */
  const handleChangeFilter = () => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <AlterFilter
          handleCancel={() => setModalBoxStatus('close')}
          oldFilterData={filter}
          handleSubmitFilter={handleSubmitFilter}
        />
      ),
      notificationModal: false,
      header: false,
    });
  };
  /**
   * Renders the custom data for the alert column
   * @param {AvailableDevice} alert - The device data
   * @returns {JSX.Element} The rendered alert cell
   */
  const renderAlert = (alert: AvailableAlerts) => {
    return (
      <div>
        <div className="alert-icon">
          <AlertIcon />
          <span style={{ fontWeight: '700' }}>{alert.actions}</span>
        </div>
      </div>
    );
  };

  /**
   * Renders the custom data for the alert column
   * @param {AvailableDevice} alert - The device data
   * @returns {JSX.Element} The rendered alert cell
   */
  const actionAlert = () => {
    return (
      <div>
        <div className="alert-icon">
          {theme === 'light' ? <ActionAlert /> : <ActionAlertDark />}
        </div>
      </div>
    );
  };
  const column = [
    {
      label: 'S. No',
      accessorKey: 'id' as keyof AvailableAlerts,
      columnWidth: 40,
    },
    {
      label: 'Date',
      accessorKey: 'date' as keyof AvailableAlerts,
      sortAllow: true,
    },
    {
      label: 'Time',
      accessorKey: 'time' as keyof AvailableAlerts,
      sortAllow: true,
    },
    {
      label: 'Station Name',
      accessorKey: 'name' as keyof AvailableAlerts,
      sortAllow: true,
    },
    {
      label: 'Device Id',
      accessorKey: 'deviceId' as keyof AvailableAlerts,
    },
    {
      label: 'Alerts',
      accessorKey: 'alerts' as keyof AvailableAlerts,
      customData: renderAlert,
    },
    {
      label: 'Actions',
      accessorKey: 'actions' as keyof AvailableAlerts,
      customData: actionAlert,
      columnWidth: 40,
    },
  ];

  useEffect(() => {
    if (alerts.length === 0) {
      fetchData({
        ...filter,
        limit: DefaultApiLimit.limit,
        page: DefaultApiLimit.page,
      });
    }
  }, []);

  return (
    <div className="alert-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={alerts}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          filterAllow={false}
          handleSortChange={handleSortFunction}
          filterFunction={handleChangeFilter}
          filterClearFunction={handleClearFilter}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
        />
      </div>
    </div>
  );
}

export default Alerts;
